<template>
  <div>
    <!-- Page header + filters inline -->
    <div
      class="d-flex align-center flex-wrap mb-5"
      style="gap: 12px;"
    >
      <div class="me-auto">
        <p class="text-2xl font-weight-bold mb-1">
          Saldo
        </p>
        <p class="text-xs text--secondary mb-0">
          Acompanhe o saldo, tarifas e movimentos por cliente
        </p>
      </div>
      <v-autocomplete
        v-model="filterUser"
        :items="users"
        label="Usuário"
        outlined
        dense
        hide-details
        clearable
        item-text="value"
        item-value="id"
        style="max-width: 320px; min-width: 220px;"
        @change="findUserTaxes"
      >
        <template #prepend-inner>
          <v-icon
            size="16"
            class="mt-1 me-1"
          >
            {{ icons.mdiAccountOutline }}
          </v-icon>
        </template>
      </v-autocomplete>
      <date
        v-model="filterYearMonth"
        :type="'month'"
        outlined
        dense
        hide-details
        label="Mês"
        readonly
        style="max-width: 180px;"
        @input="getExtract"
      />
    </div>

    <!-- Balance + Tariffs row -->
    <v-row
      class="mb-2"
      align="stretch"
    >
      <v-col
        cols="12"
        md="4"
        class="d-flex flex-column"
      >
        <div class="section-label mb-2">
          Saldo
        </div>
        <wallet-extract-balance-card
          :available-limit="availableLimit ?? 0"
          class="flex-grow-1"
        />
      </v-col>
      <v-col
        v-if="dados.length"
        cols="12"
        md="8"
      >
        <div class="section-label mb-2">
          Tarifa dos Serviços
        </div>
        <wallet-extract-statistics-card :dados="dados" />
      </v-col>
    </v-row>

    <!-- Movements -->
    <div class="d-flex align-center mt-5 mb-3">
      <div>
        <p class="text-xl font-weight-bold mb-0">
          Movimentos
        </p>
        <p class="text-xs text--secondary mb-0">
          Histórico de débitos e recargas
        </p>
      </div>
    </div>

    <v-card outlined>
      <v-data-table
        :headers="headers"
        :items="movements"
        item-key="id"
        disable-sort
        class="movements-table"
      >
        <template #[`item.action`]="{ item }">
          <v-btn
            color="info"
            icon
            small
            title="Visualizar"
            @click="setExtractDetail(item)"
          >
            <v-icon size="18">
              {{ icons.mdiEye }}
            </v-icon>
          </v-btn>
        </template>

        <template #[`item.id`]="{ item }">
          <code class="txn-id">#{{ resolveType(item.type).prefix }}{{ item.id }}</code>
        </template>

        <template #[`item.walletMovements[0].description`]="{ item }">
          <span
            v-if="item.walletMovements && item.walletMovements.length"
            class="description-cell"
          >
            {{ item.walletMovements[0].description }}
          </span>
          <span
            v-else
            class="text--secondary text-caption"
          >—</span>
        </template>

        <template #[`item.type`]="{ item }">
          <v-chip
            small
            :outlined="item.type !== 'recharge'"
            :color="item.type === 'recharge' ? 'success' : 'primary'"
            class="type-chip"
          >
            <v-icon
              left
              size="13"
            >
              {{ resolveType(item.type).icon }}
            </v-icon>
            {{ resolveType(item.type).text }}
          </v-chip>
        </template>

        <template #[`item.createdAt`]="{ item }">
          <div class="date-cell">
            <span>{{ $day(item.createdAt).format('DD/MM/YYYY') }}</span>
            <span class="text--secondary">{{ $day(item.createdAt).format('HH:mm') }}</span>
          </div>
        </template>

        <template #[`item.value`]="{ item }">
          <span
            :class="[resolveBalanceClass(item.value), 'value-cell']"
          >
            <span class="value-sign">{{ resolveBalanceSign(item.value) }}</span>
            R$ {{ $money(Math.abs(item.value), 2) }}
          </span>
        </template>
      </v-data-table>
    </v-card>

    <!-- Movement detail dialog -->
    <dialog-form
      :status="dialogExtractDetail"
      :title="`Movimento #${extractDetail.id}`"
      :width="1000"
      :hide-actions="true"
      @close="dialogExtractDetail = !dialogExtractDetail"
    >
      <v-row dense>
        <v-col
          cols="12"
          md="3"
          sm="6"
        >
          <v-text-field
            :value="$day(extractDetail.createdAt).format('DD/MM/YYYY HH:mm:ss')"
            disabled
            outlined
            hide-details
            dense
            label="Data"
          />
        </v-col>
        <v-col
          cols="12"
          md="3"
          sm="6"
        >
          <v-text-field
            :value="resolveType(extractDetail.type).text"
            :append-icon="resolveType(extractDetail.type).icon"
            disabled
            outlined
            hide-details
            dense
            label="Tipo"
          />
        </v-col>
        <v-col
          cols="12"
          md="3"
          sm="6"
        >
          <v-text-field
            :value="$money(extractDetail.value, 2)"
            disabled
            outlined
            hide-details
            dense
            label="Valor (R$)"
            prefix="R$"
          />
        </v-col>
      </v-row>

      <v-row class="mt-3">
        <v-col cols="12">
          <v-data-table
            :headers="headersDetail"
            :items="extractDetail.walletMovements"
            item-key="id"
            disable-sort
            dense
            hide-default-footer
          >
            <template #[`item.createdAt`]="{ item }">
              {{ $day(item.createdAt).format('DD/MM/YYYY HH:mm:ss') }}
            </template>
            <template #[`item.value`]="{ item }">
              {{ $money(item.value, 2) }}
            </template>
          </v-data-table>
        </v-col>
      </v-row>

      <v-divider class="my-4" />

      <v-row>
        <v-col cols="12">
          <v-btn
            color="primary"
            @click="dialogAddMovement = true"
          >
            <v-icon
              left
              size="18"
            >
              {{ icons.mdiFilePlus }}
            </v-icon>
            Adicionar Movimento
          </v-btn>
        </v-col>
      </v-row>
    </dialog-form>

    <!-- Add movement dialog -->
    <dialog-form
      :status="dialogAddMovement"
      :title="'Adicionar Movimento'"
      :width="800"
      @action="addMovement"
      @close="dialogAddMovement = !dialogAddMovement"
    >
      <validation-observer
        ref="formAddMovement"
        autocomplete="off"
        tag="form"
      >
        <v-row>
          <v-col
            class="pb-0"
            cols="12"
            sm="12"
            md="6"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Tipo"
              rules="required"
              vid="extractMovement.type"
            >
              <v-select
                v-model="extractMovement.type"
                :error-messages="errors"
                :items="[
                  { value: 'input', text: 'Entrada' },
                  { value: 'output', text: 'Saída' },
                ]"
                label="Tipo"
                item-text="text"
                item-value="value"
                outlined
                dense
                :hide-details="!errors.length"
              />
            </validation-provider>
          </v-col>
          <v-col
            class="pb-0"
            cols="12"
            sm="12"
            md="6"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Valor"
              rules="required"
              vid="extractMovement.value"
            >
              <v-text-field
                v-model="extractMovement.value"
                :error-messages="errors"
                label="Valor"
                type="number"
                outlined
                dense
                :hide-details="!errors.length"
              />
            </validation-provider>
          </v-col>
          <v-col
            class="pb-0"
            cols="12"
            sm="12"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Motivo"
              rules="required|max:250"
              vid="extractMovement.description"
            >
              <v-textarea
                v-model="extractMovement.description"
                :error-messages="errors"
                label="Motivo"
                outlined
                dense
                :hide-details="!errors.length"
              />
            </validation-provider>
          </v-col>
        </v-row>
      </validation-observer>
    </dialog-form>
  </div>
</template>

<script>
import {
  mdiEye,
  mdiFilePlus,
  mdiArrowUp,
  mdiArrowDown,
  mdiAccountOutline,
  mdiBullhornOutline,
  mdiCashPlus,
} from '@mdi/js'
import { mapActions, mapState } from 'vuex'
import { ref } from 'vue'

import WalletExtractStatisticsCard from '@/components/wallet-extract-statistics-card/WalletExtractStatisticsCard.vue'
import WalletExtractBalanceCard from '@/components/wallet-extract-balance-card/WalletExtractBalanceCard.vue'

export default {
  components: {
    WalletExtractStatisticsCard,
    WalletExtractBalanceCard,
  },
  computed: {
    ...mapState('customerTaxes', ['customTaxes']),
    dados() {
      return this.customTaxes.map(item => {
        const service = this.services.find(i => i.id === item.serviceId)

        return {
          title: service ? service.name : '',
          total: item.unitaryValue,
        }
      })
    },
  },
  setup() {
    const users = ref([])
    const filterUser = ref(null)
    const filterYearMonth = ref(null)
    const extractDetail = ref({})
    const extractMovement = ref({})
    const dialogExtractDetail = ref(false)
    const dialogAddMovement = ref(false)
    const total = ref([])
    const movements = ref([])
    const services = ref([])
    const availableLimit = ref(null)

    const resolveType = value => {
      if (value === 'recharge') {
        return {
          prefix: 'R',
          icon: mdiCashPlus,
          color: 'success',
          text: 'Recarga',
        }
      }

      return {
        prefix: 'C',
        icon: mdiBullhornOutline,
        color: 'primary',
        text: 'Campanha',
      }
    }

    const resolveBalanceClass = value => (value <= 0 ? 'error--text' : 'success--text')
    const resolveBalanceSign = value => (value <= 0 ? '−' : '+')

    return {
      availableLimit,
      users,
      filterUser,
      filterYearMonth,
      extractDetail,
      extractMovement,
      dialogExtractDetail,
      dialogAddMovement,
      services,
      resolveType,
      resolveBalanceClass,
      resolveBalanceSign,
      icons: {
        mdiEye,
        mdiFilePlus,
        mdiAccountOutline,
        mdiBullhornOutline,
        mdiCashPlus,
      },
      headers: [
        {
          text: 'Ações', value: 'action', align: 'center', width: '70px',
        },
        { text: 'Transação', value: 'id', width: '140px' },
        { text: 'Descrição', value: 'walletMovements[0].description' },
        { text: 'Tipo', value: 'type', width: '140px' },
        { text: 'Data', value: 'createdAt', width: '170px' },
        {
          text: 'Valor (R$)', value: 'value', align: 'right', width: '130px',
        },
      ],
      headersDetail: [
        { text: 'Data', value: 'createdAt' },
        { text: 'Descrição', value: 'description' },
        {
          text: 'Valor (R$)', value: 'value', align: 'right',
        },
      ],
      movements,
      total,
    }
  },

  async created() {
    await this.findUsers()
    await this.findServices()
  },

  beforeDestroy() {
    this.clearCustomerTaxes()
    this.services = []
    this.filterUser = null
  },

  methods: {
    ...mapActions('customerTaxes', {
      findCustomerTaxes: 'findCustomTaxes',
      clearCustomerTaxes: 'clearCustomerTaxes',
    }),
    ...mapActions('services', {
      fetchServices: 'find',
    }),
    ...mapActions('user', {
      fetchUsers: 'find',
    }),
    ...mapActions('walletExtract', {
      fechWalletExtract: 'find',
      createMovement: 'create',
    }),

    setExtractDetail(item) {
      this.extractDetail = item
      this.dialogExtractDetail = true
    },

    async findUserTaxes() {
      this.getExtract()
      await this.findCustomerTaxes({ userId: this.filterUser })
    },

    async findUsers() {
      const users = await this.fetchUsers()
      this.users = users.map(user => ({
        ...user,
        value: `${user.document ? String(this.$cpfCnpj.toMask(user.document)).concat(' - ', user.name) : user.name}`,
      }))
    },

    async findServices() {
      this.services = await this.fetchServices()
    },

    async getExtract() {
      const { total, wallets } = await this.fechWalletExtract({
        userId: this.filterUser,
        yearMonth: this.filterYearMonth
          ? this.$day(this.filterYearMonth, 'MM/YYYY').format('YYYY-MM')
          : undefined,
      })
      this.availableLimit = total.availableLimit ?? 0
      this.movements = wallets
    },

    async addMovement() {
      const body = {
        walletId: this.extractDetail.id,
        description: this.extractMovement.description,
        value: Number(this.extractMovement.value),
      }

      body.value = this.extractMovement.type === 'output' ? -body.value : body.value

      if (await this.$refs.formAddMovement.validate()) {
        const status = await this.createMovement(body)

        if (status === 201) {
          await this.getExtract()
          const item = this.movements.find(movement => movement.id === this.extractDetail.id)
          this.setExtractDetail(item)

          this.extractMovement.type = null
          this.extractMovement.description = null
          this.extractMovement.value = null
          this.dialogAddMovement = false
        }
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.section-label {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  opacity: 0.5;
}

.txn-id {
  font-family: 'Fira Mono', 'Courier New', monospace;
  font-size: 0.78rem;
  background: transparent;
  color: inherit;
  opacity: 0.6;
  padding: 0;
}

.description-cell {
  font-size: 0.875rem;
  font-weight: 500;
}

.date-cell {
  display: flex;
  flex-direction: column;
  line-height: 1.3;
  font-size: 0.8rem;

  span:first-child {
    font-weight: 500;
  }

  span:last-child {
    font-size: 0.72rem;
    opacity: 0.55;
  }
}

.value-cell {
  font-size: 0.88rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 2px;
}

.value-sign {
  font-size: 0.95rem;
  line-height: 1;
  margin-right: 1px;
}

.type-chip {
  border-radius: 4px !important;

  ::v-deep .v-chip__content {
    font-size: 0.72rem;
    letter-spacing: 0.02em;
  }
}

::v-deep .movements-table {
  .v-data-table__wrapper tbody tr {
    transition: background 0.15s ease;

    &:hover {
      background: rgba(6, 241, 26, 0.04) !important;
    }
  }
}
</style>
